// @flow
import { HEADER_HEIGHT } from '@/constants'
import { trackPageView } from '@/utils/analytics'

type PageMeta = {
  title: string,
  desc?: string,
  scrollTo: string,
  scrollBehavior: string,
  scrollDelay: number,
  focusTitle: boolean,
  gaTrack: boolean,
}

/**
 * Manually update page title and desc, focus and scroll to the top of the page
 * - for SPA routes, will focus #spa-page-title or fall back to #app
 * - will smooth scroll to top of page
 * @param title
 * @param desc
 * @param scrollTo
 * @param scrollBehavior
 * @param scrollDelay
 * @param focusTitle
 * @param gaTrack
 */
export const spaMetaUpdateAndTrack = ({
  title = '',
  desc,
  scrollTo = 'top',
  scrollBehavior = 'instant', // smooth and auto supported
  scrollDelay = 0, // used to wrap a setTimeout before scrolling
  focusTitle = true,
  gaTrack = false,
}: PageMeta = {}) => {
  /* TITLE TAG UPDATES */
  const titleEl = document.querySelector('head > title')

  if (title && titleEl) {
    titleEl.innerHTML = `${title} | Fitness Blender`
  }
  // end title

  /* SCROLL TO TOP - DEFAULT */
  // test for native smooth scroll options
  // eslint-disable-next-line
  const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style

  if (scrollTo === 'top') {
    const scrollToTop = () => {
      if (supportsNativeSmoothScroll) {
        window.scrollTo({ top: 0, behavior: scrollBehavior })
      } else {
        window.scrollTo(0, 0)
      }
    }

    if (scrollDelay) {
      // if a delay is needed between routes for whatever reason before scrolling
      // pass in scrollDelay or manage scroll on the primary SPA page component
      // itself.
      //
      // could be useful if scrolling to SPA elem that has not rendered yet
      setTimeout(scrollToTop, scrollDelay)
    } else {
      scrollToTop()
    }
  }
  // end scroll to top

  // scroll to query selector element
  if (scrollTo && scrollTo !== 'top') {
    const scrollToElem = () => {
      const scrollEl = document.querySelector(`${scrollTo}`)

      if (scrollEl) {
        const scrollToY = scrollEl.offsetTop - HEADER_HEIGHT

        if (supportsNativeSmoothScroll) {
          window.scrollTo({ top: scrollToY, behavior: scrollBehavior })
        } else {
          window.scrollTo(0, scrollToY)
        }
      }
    }

    if (scrollDelay) {
      // if a delay is needed between routes for whatever reason before scrolling
      // pass in scrollDelay or manage scroll on the primary SPA page component
      // itself.
      //
      // could be useful if scrolling to SPA elem that has not rendered yet
      setTimeout(scrollToElem, scrollDelay)
    } else {
      scrollToElem()
    }
  }

  // focus primary title
  if (focusTitle) {
    // delayed focus to allow for rendering
    setTimeout(() => {
      const focusEl = document.querySelector('#spa-title')

      if (focusEl) {
        // also focus, but not keyboard tab
        focusEl.style.outline = 'none'
        focusEl.setAttribute('tabindex', '-1')
        focusEl.focus()
      }
    }, 250)
  }

  // manually push to GA
  // let XHRs settle before sending
  setTimeout(() => {
    if (gaTrack) {
      trackPageView()
    }
  }, 150)
}
